// ccrUploadClient — the DEFERRED CC&R-upload surface (PUBLIC, token-gated).
//
// The low-friction counterpart to the owner intake: the intake captures ONLY the
// owner's HOA CHOICE (no document). When the owner names a NEW/unlisted HOA, we email
// them a tokenized link to /ccr-upload/:token, where they upload the CC&R PDF later.
//
// The token binds the ONE newly-created HOA (server-side); there is NO client-supplied
// hoaId, so the upload can only ever attach to that HOA — never an existing/shared one.
//
//   - GET  /api/ccr-upload/:token   → CcrUploadView   (page state)
//   - POST /api/ccr-upload/:token   (multipart)       → { ok, received }
//
// Talks directly to fetch (NOT the authed `api` helper) — the token in the path is the
// only credential (same shape as ownerIntakeClient / moveoutFundsClient).

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

/** The page-state view returned by GET /api/ccr-upload/:token. */
export interface CcrUploadView {
  /**
   * 'open'        — ready to accept a PDF.
   * 'done'        — a CC&R is already on file / the HOA is confirmed → "we've got it".
   * 'unavailable' — bad/dormant token, or the token's HOA is no longer uploadable.
   */
  state: 'open' | 'done' | 'unavailable';
  /** The HOA the CC&R is for (display). */
  hoaName: string;
  /** The property this HOA is for (display). */
  propertyAddress: string;
}

/** Error carrying the parsed body (mirrors OwnerIntakeApiError). */
export class CcrUploadApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`CC&R upload API error ${status}`);
    this.name = 'CcrUploadApiError';
  }

  get errorMessage(): string | null {
    return (this.body as { error?: string } | null)?.error ?? null;
  }
}

async function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* empty */
    }
    throw new CcrUploadApiError(res.status, body);
  }
  return (await res.json()) as T;
}

/** GET the CC&R-upload page state (token-gated). */
export function getCcrUpload(token: string): Promise<CcrUploadView> {
  return publicRequest<CcrUploadView>(`/api/ccr-upload/${encodeURIComponent(token)}`);
}

/**
 * Upload the CC&R PDF (token-gated). The target HOA is resolved from the token — there
 * is no hoaId input. Multipart; FormData sets its own boundary. Resolves when received
 * ("we'll take it from here").
 */
export function uploadCcr(
  token: string,
  file: File,
): Promise<{ ok: boolean; received: boolean }> {
  const form = new FormData();
  form.append('file', file);
  return publicRequest<{ ok: boolean; received: boolean }>(
    `/api/ccr-upload/${encodeURIComponent(token)}`,
    { method: 'POST', body: form },
  );
}
